import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PullToRefresh from '../components/PullToRefresh';
import CreateDocumentModal from '../components/documents/CreateDocumentModal';
import documentService from '../services/documentService';
import { usePermissionAccess, CREATE_DOCUMENT_PERMISSION } from '../utils/features';
import '../assets/styles/documents.css';
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  DatePicker,
  Empty,
  Form,
  Input,
  Image,
  List,
  Modal,
  Select,
  Skeleton,
  Space,
  Spin,
  Tabs,
  Typography,
  Upload,
  message
} from 'antd';
import {
  CarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileImageOutlined,
  ReloadOutlined,
  FileTextOutlined,
  MessageOutlined,
  PhoneOutlined,
  PlayCircleOutlined,
  HistoryOutlined,
  ShareAltOutlined,
  InfoCircleOutlined,
  ToolOutlined,
  UploadOutlined,
  UserOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import eventService from '../services/eventService';
import breakdownCategoryService from '../services/breakdownCategoryService';
import EventCard from '../components/EventCard';
import BreakdownCategoryPanels from '../components/categories/BreakdownCategoryPanels';
import TyrePositionPicker from '../components/categories/TyrePositionPicker';
import { loadMechanicStatuses } from '../services/mechanicStatusService';
import { getCurrentLocation } from '../utils/locationUtils';
import { BREAKDOWN_STATUS_COLORS } from '../utils/constants';
import { getUserId, getUserName, getUserRole } from '../utils/tokenUtils';
import imageCacheManager, {
  cacheImages,
  compareImageIds,
  getCachedImages,
  isCacheValid
} from '../utils/imageCacheUtils';
import '../assets/styles/eventDetails.css';
import '../assets/styles/categoryFaults.css';

const { Text, Title } = Typography;

const invalidDate = '0001-01-01T00:00:00+00:00';

const EXT_TO_MIME = {
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
  mp4: 'video/mp4',
  mov: 'video/quicktime'
};

const formatDate = (value, includeYear = true) => {
  if (!value || value === invalidDate) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('en-ZA', {
    day: '2-digit',
    month: 'short',
    ...(includeYear ? { year: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
};

const getStatusColor = (value) => {
  const text = String(value || '').toLowerCase();
  if (text.includes('cancel')) return '#ff4d4f';
  if (text.includes('resolved') || text.includes('complete')) return '#52c41a';
  if (text.includes('progress') || text.includes('workshop')) return '#1677ff';
  if (text.includes('pending') || text.includes('created')) return '#faad14';
  return '#8c8c8c';
};

const cleanValue = (value) => {
  if (value === undefined || value === null || value === '') return 'N/A';
  return value;
};

const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const getDialablePhone = (phoneNumber) => String(phoneNumber || '').replace(/[^\d+]/g, '');

const openNavigation = ({ latitude, longitude, address }) => {
  const hasCoordinates = latitude !== undefined && latitude !== null && latitude !== '' &&
    longitude !== undefined && longitude !== null && longitude !== '';
  const query = hasCoordinates ? `${latitude},${longitude}` : address;

  if (!query) {
    message.warning('No event location available');
    return;
  }

  const encodedQuery = encodeURIComponent(query);
  const userAgent = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  const isAndroid = /android/i.test(userAgent);
  const isHuawei = /huawei/i.test(userAgent);

  if (hasCoordinates && isIOS) {
    window.location.href = `maps://?ll=${latitude},${longitude}&q=${latitude},${longitude}`;
    return;
  }

  if (hasCoordinates && (isAndroid || isHuawei || /mobile/i.test(userAgent))) {
    window.location.href = `geo:${latitude},${longitude}?q=${latitude},${longitude}`;
    return;
  }

  window.open(`https://www.google.com/maps/search/?api=1&query=${encodedQuery}`, '_blank', 'noopener,noreferrer');
};

const buildEventShareText = ({ reference, breakdown, address }) => [
  `Event: ${reference || 'Unknown'}`,
  breakdown?.description ? `Description: ${breakdown.description}` : null,
  address ? `Address: ${address}` : null,
  breakdown?.driverAlias ? `Driver: ${breakdown.driverAlias}` : null,
  breakdown?.driverCell ? `Contact: ${breakdown.driverCell}` : null
].filter(Boolean).join('\n');

const getCurrentDateTime = () => {
  const now = new Date();
  const date = now.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
  const time = now.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  return `${date} ${time}`;
};

const getMediaTypeFromFile = (file) => {
  if (file?.type) return file.type;
  const extension = String(file?.name || '').split('.').pop()?.toLowerCase();
  return EXT_TO_MIME[extension] || 'application/octet-stream';
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getImageMimeFromBase64 = (base64 = '') => {
  if (base64.startsWith('iVBORw0KGgo')) return 'image/png';
  if (base64.startsWith('UklGR')) return 'image/webp';
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  return 'image/jpeg';
};

const getMediaDataUrl = (item) => {
  if (!item?.base64) return '';
  const mediaType = String(item.mediaType || '').toLowerCase();

  if (mediaType.startsWith('image')) {
    return `data:${getImageMimeFromBase64(item.base64)};base64,${item.base64}`;
  }

  if (mediaType.startsWith('video')) {
    return `data:${mediaType || 'video/mp4'};base64,${item.base64}`;
  }

  if (mediaType.startsWith('pdf') || mediaType === 'application/pdf') {
    return `data:application/pdf;base64,${item.base64}`;
  }

  return '';
};

const addLocationTimeOverlay = async (imageFile) => {
  try {
    const location = await getCurrentLocation();
    const dateTime = getCurrentDateTime();
    const imageUrl = URL.createObjectURL(imageFile);

    return await new Promise((resolve) => {
      const image = document.createElement('img');

      image.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = image.width;
        canvas.height = image.height;
        context.drawImage(image, 0, 0);

        const fontSize = Math.max(18, Math.floor(canvas.width / 42));
        // Location may be null when it is off/denied — stamp the time either way,
        // and only add the coordinates when there is a real fix (prevents a crash
        // that used to leave the promise unresolved and hang the upload).
        const hasFix =
          location &&
          Number.isFinite(location.latitude) &&
          Number.isFinite(location.longitude);
        const lines = [dateTime];
        if (hasFix) {
          lines.push(`Lat: ${Number(location.latitude).toFixed(6)}`);
          lines.push(`Lng: ${Number(location.longitude).toFixed(6)}`);
        }
        const padding = Math.max(12, Math.floor(canvas.width / 80));
        const lineHeight = fontSize + 8;
        const boxHeight = lineHeight * lines.length + padding;

        context.fillStyle = 'rgba(0, 0, 0, 0.55)';
        context.fillRect(0, canvas.height - boxHeight, canvas.width, boxHeight);
        context.fillStyle = '#fff';
        context.font = `${fontSize}px Arial`;
        lines.forEach((line, index) => {
          context.fillText(line, padding, canvas.height - boxHeight + padding + fontSize + index * lineHeight);
        });

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(imageUrl);
          if (!blob) {
            resolve(imageFile);
            return;
          }
          resolve(new File([blob], imageFile.name, { type: imageFile.type, lastModified: Date.now() }));
        }, imageFile.type || 'image/jpeg', 0.92);
      };

      image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(imageFile);
      };

      image.src = imageUrl;
    });
  } catch {
    return imageFile;
  }
};

const formatCustomDisplayValue = (value) => {
  if (value === undefined || value === null || value === '') return 'N/A';
  if (Array.isArray(value)) {
    return value.length > 0
      ? value.map((item) => formatCustomDisplayValue(item)).join(', ')
      : 'N/A';
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsedArray = JSON.parse(trimmed);
        if (Array.isArray(parsedArray)) {
          return formatCustomDisplayValue(parsedArray);
        }
      } catch {
        // Fall through to normal string formatting.
      }
    }

    const parsed = dayjs(value);
    if (/^\d{4}-\d{2}-\d{2}T/.test(value) && parsed.isValid()) {
      return parsed.format('DD MMM YYYY HH:mm');
    }
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const getStoredUserData = () => {
  try {
    return JSON.parse(localStorage.getItem('user_data') || '{}');
  } catch {
    return {};
  }
};

const idsMatch = (left, right) => {
  if (left === undefined || left === null || right === undefined || right === null) return false;
  return String(left).toLowerCase() === String(right).toLowerCase();
};

const buildCategorySearchLabel = (item) =>
  [item.categoryName, item.subCategoryName, item.componentName].filter(Boolean).join(' | ');

const buildCategorySearchValue = (item) =>
  [item.categoryId, item.subCategoryId, item.componentId].filter(Boolean).join('|');

const upsertById = (items, item) => {
  if (!item?.id) return items;
  const exists = items.some((existing) => String(existing.id) === String(item.id));
  return exists
    ? items.map((existing) => String(existing.id) === String(item.id) ? { ...existing, ...item } : existing)
    : [...items, item];
};

const getSupplierMechanicId = (mechanic) => mechanic?.id;

const getAssignedMechanicId = (mechanic) => mechanic?.mechanicId ?? mechanic?.id;

const getDisplayedMechanicId = (mechanic) => mechanic?.mechanicId ?? mechanic?.mechanicModelId ?? mechanic?.id;

const getBreakdownId = (breakdown, fallbackId) =>
  breakdown?.id ?? fallbackId;

const parseAssignedMechanics = (eventData) => {
  return Array.isArray(eventData?.assignedMechanics) ? eventData.assignedMechanics : [];
};

const DetailTable = ({ children }) => (
  <div className="event-detail-table">{children}</div>
);

const DetailValue = ({ label, values = [], children }) => (
  <div className="event-detail-row">
    <div className="event-detail-label">{label}</div>
    <div className="event-detail-value">{children ?? cleanValue(firstPresent(...values))}</div>
  </div>
);

const EventDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedId = location.state?.selectedId || id;
  const [eventData, setEventData] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [customValues, setCustomValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusSaving, setStatusSaving] = useState(false);
  const [etaModalOpen, setEtaModalOpen] = useState(false);
  const [etaDate, setEtaDate] = useState(null);
  const [etaHour, setEtaHour] = useState(null);
  const [etaMinute, setEtaMinute] = useState(null);
  const [etaSaving, setEtaSaving] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [media, setMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [createDocumentOpen, setCreateDocumentOpen] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);
  const [mechanicStatuses, setMechanicStatuses] = useState([]);
  const [mechanicDetails, setMechanicDetails] = useState(null);
  const [mechanicActionType, setMechanicActionType] = useState(null);
  const [mechanicActionSaving, setMechanicActionSaving] = useState(false);
  const [assignedMechanics, setAssignedMechanics] = useState([]);
  const [assignedMechanicsLoading, setAssignedMechanicsLoading] = useState(false);
  const [mechanicOptions, setMechanicOptions] = useState([]);
  const [mechanicOptionsLoading, setMechanicOptionsLoading] = useState(false);
  const [selectedMechanicId, setSelectedMechanicId] = useState(null);
  const [assigningMechanic, setAssigningMechanic] = useState(false);
  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [completeSaving, setCompleteSaving] = useState(false);
  const [breakdownCategories, setBreakdownCategories] = useState([]);
  const [breakdownSubcategories, setBreakdownSubcategories] = useState([]);
  const [breakdownComponents, setBreakdownComponents] = useState([]);
  const [breakdownCauses, setBreakdownCauses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedCause, setSelectedCause] = useState(null);
  const [selectedTyres, setSelectedTyres] = useState([]);
  const [tyrePickerOpen, setTyrePickerOpen] = useState(false);
  const [completeCategorySearchTerm, setCompleteCategorySearchTerm] = useState('');
  const [completeCategorySearchValue, setCompleteCategorySearchValue] = useState(undefined);
  const [completeCategorySearchResults, setCompleteCategorySearchResults] = useState([]);
  const [completeCategorySearching, setCompleteCategorySearching] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [loadingCauses, setLoadingCauses] = useState(false);
  const [completeFormData, setCompleteFormData] = useState({});
  const [noteForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [completeForm] = Form.useForm();
  const userRole = getUserRole();
  const normalizedUserRole = String(userRole || '').toUpperCase();
  const isMechanic = normalizedUserRole === 'MECHANIC';
  const canSeeSuppliers = normalizedUserRole === 'SUPPLIER' || normalizedUserRole === 'OWNER';
  const userName = getUserName();
  const userId = getUserId();

  const mechStatusOptions = useMemo(
    () => mechanicStatuses.map((s) => ({ value: s.id, label: s.name })),
    [mechanicStatuses]
  );



  const breakdown = eventData?.breakdown;
  const currentBreakdownId = getBreakdownId(breakdown, selectedId);
  const assignedMechanicBreakdownIds = useMemo(() => {
    const candidates = [
      breakdown?.id
    ];
    const seen = new Set();
    return candidates.filter((candidate) => {
      if (candidate === undefined || candidate === null || candidate === '') return false;
      const key = String(candidate).toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [breakdown?.id]);
  const eventTypeId = customValues?.ids?.eventTypeId ?? breakdown?.eventTypeId;
  const notes = useMemo(() => {
    const rows = Array.isArray(eventData?.notes) ? eventData.notes : [];
    return [...rows].sort((a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime());
  }, [eventData?.notes]);

  const canCompleteBreakdown = ['MECHANIC', 'CONSULTANT', 'OWNER', 'ADMIN'].includes(normalizedUserRole);
  const customDisplayEntries = useMemo(
    () => Object.entries(customValues?.display || {}),
    [customValues?.display]
  );

  const loadDetails = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const [details, status] = await Promise.all([
        eventService.getEventDetails(selectedId),
        eventService.getCurrentEventStatus(selectedId)
      ]);
      setEventData(details);
      setAssignedMechanics(canSeeSuppliers ? [] : parseAssignedMechanics(details));
      setCurrentStatus(status);

      const fieldValues = await eventService.getCustomFieldValues(selectedId).catch(() => null);
      setCustomValues(fieldValues);

      const typeId = fieldValues?.ids?.eventTypeId ?? details?.breakdown?.eventTypeId;
      if (typeId) {
        const statusRows = await eventService.getEventStatuses(typeId);
        setStatuses(statusRows);
      } else {
        setStatuses([]);
      }
    } catch (error) {
      console.error('Failed to load event details:', error);
      message.error('Failed to load event details');
    } finally {
      setLoading(false);
    }
  }, [canSeeSuppliers, selectedId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const handleRefresh = useCallback(() => loadDetails(), [loadDetails]);

  const fetchEventHistory = useCallback(async () => {
    if (!breakdown || !currentBreakdownId) {
      setHistoryRows([]);
      return;
    }

    setLoadingHistory(true);
    try {
      const rows = await eventService.getEventHistory({
        breakdownId: currentBreakdownId,
        registrationNumber: breakdown?.registrationNumber,
        trailerRegistrationNumber: breakdown?.trailerRegistration || breakdown?.trailerReg,
        fleetNumber: breakdown?.fleetNumber
      });
      setHistoryRows(rows);
    } catch (error) {
      console.error('Failed to load event history:', error);
      setHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [breakdown, currentBreakdownId]);

  useEffect(() => {
    fetchEventHistory();
  }, [fetchEventHistory]);

  useEffect(() => {
    if (!isMechanic && !canSeeSuppliers) return;
    loadMechanicStatuses().then(setMechanicStatuses);
  }, [canSeeSuppliers, isMechanic]);

  useEffect(() => {
    if (!canSeeSuppliers) return;

    setMechanicOptionsLoading(true);
    eventService.getSupplierMechanics()
      .then((rows) => setMechanicOptions(rows || []))
      .catch((error) => {
        console.error('Failed to load supplier mechanics:', error);
        message.error('Failed to load mechanics list');
      })
      .finally(() => setMechanicOptionsLoading(false));
  }, [canSeeSuppliers]);

  const fetchAssignedMechanics = useCallback(async () => {
    if (!canSeeSuppliers || assignedMechanicBreakdownIds.length === 0) return;

    setAssignedMechanicsLoading(true);
    try {
      const supplierMechanicIds = new Set(
        mechanicOptions
          .map((mechanic) => getSupplierMechanicId(mechanic))
          .filter((value) => value !== undefined && value !== null)
          .map((value) => String(value).toLowerCase())
      );

      for (const breakdownIdCandidate of assignedMechanicBreakdownIds) {
        const companyRows = await eventService.getCompanyAssignedMechanics(breakdownIdCandidate);
        if (companyRows?.length > 0) {
          setAssignedMechanics(companyRows);
          return;
        }
      }

      let supplierRows = [];
      for (const breakdownIdCandidate of assignedMechanicBreakdownIds) {
        const allRows = await eventService.getMechanicDetails(breakdownIdCandidate);
        supplierRows = supplierMechanicIds.size > 0
          ? allRows.filter((mechanic) => {
            const mechanicId = mechanic.id;
            return mechanicId !== undefined &&
              mechanicId !== null &&
              supplierMechanicIds.has(String(mechanicId).toLowerCase());
          })
          : allRows;

        if (supplierRows.length > 0) break;
      }

      setAssignedMechanics(supplierRows);
    } catch (error) {
      console.error('Failed to load assigned event mechanics:', error);
    } finally {
      setAssignedMechanicsLoading(false);
    }
  }, [assignedMechanicBreakdownIds, canSeeSuppliers, mechanicOptions]);

  useEffect(() => {
    fetchAssignedMechanics();
  }, [fetchAssignedMechanics]);

  const fetchMechanicDetails = useCallback(async () => {
    if (!isMechanic || !currentBreakdownId || !userId) {
      setMechanicDetails(null);
      return;
    }

    try {
      const rows = await eventService.getMechanicDetails(currentBreakdownId);
      const currentMechanic = rows.find((mechanic) =>
        idsMatch(mechanic.id, userId)
      );
      setMechanicDetails(currentMechanic || null);
    } catch (error) {
      console.error('Failed to load mechanic details:', error);
      setMechanicDetails(null);
    }
  }, [currentBreakdownId, isMechanic, userId]);

  useEffect(() => {
    fetchMechanicDetails();
  }, [fetchMechanicDetails]);

  useEffect(() => {
    statusForm.setFieldsValue({ status: mechanicDetails?.status });
  }, [mechanicDetails, statusForm]);

  const selectedStatusId = Form.useWatch('status', statusForm);

  const etaValue = useMemo(() => {
    if (!etaDate || etaHour === null || etaMinute === null) return null;
    return etaDate.hour(etaHour).minute(etaMinute).second(0);
  }, [etaDate, etaHour, etaMinute]);

  const selectedStatusName = useMemo(
    () => mechanicStatuses.find((status) => Number(status.id) === Number(selectedStatusId))?.name || '',
    [selectedStatusId, mechanicStatuses]
  );
  const mechanicStatusName = useMemo(() => {
    if (mechanicDetails?.status === undefined || mechanicDetails?.status === null) return '';
    return mechanicStatuses.find((status) => Number(status.id) === Number(mechanicDetails.status))?.name || '';
  }, [mechanicDetails?.status, mechanicStatuses]);
  const isMechanicPending = isMechanic && (
    mechanicStatusName.toLowerCase() === 'pending' ||
    (!mechanicStatusName && Number(mechanicDetails?.status) === 2)
  );
  const isCancelledSelected = selectedStatusName === 'Cancelled';
  const isWorkshopSelected = selectedStatusName === 'In Workshop' || selectedStatusName === 'Workshop Repair';
  const isEnRouteSelected = ['en route', 'en-route'].includes(selectedStatusName.toLowerCase());
  const isOnSiteSelected = ['on site', 'on-site'].includes(selectedStatusName.toLowerCase());
  const assignedMechanicIds = useMemo(() => new Set(
    assignedMechanics
      .map((mechanic) => getAssignedMechanicId(mechanic))
      .filter((value) => value !== undefined && value !== null)
      .map((value) => String(value).toLowerCase())
  ), [assignedMechanics]);
  const filteredMechanicOptions = useMemo(
    () => mechanicOptions.filter((mechanic) => {
      const mechanicId = getSupplierMechanicId(mechanic);
      if (mechanicId === undefined || mechanicId === null) return false;
      return !assignedMechanicIds.has(String(mechanicId).toLowerCase());
    }),
    [assignedMechanicIds, mechanicOptions]
  );
  const selectedMechanic = useMemo(
    () => mechanicOptions.find((mechanic) => idsMatch(getSupplierMechanicId(mechanic), selectedMechanicId)),
    [mechanicOptions, selectedMechanicId]
  );

  const getMechanicId = getDisplayedMechanicId;

  const getMechanicOptionMatch = (mechanic) => {
    const mechanicId = getAssignedMechanicId(mechanic);
    return mechanicOptions.find((option) => idsMatch(getSupplierMechanicId(option), mechanicId));
  };

  const getMechanicCompanyName = (mechanic) => {
    const optionMatch = getMechanicOptionMatch(mechanic);
    return mechanic?.companyName || optionMatch?.companyName || '';
  };

  const getMechanicDisplayName = (mechanic) => {
    return mechanic?.mechanicName ||
      mechanic?.fullName ||
      mechanic?.fullname ||
      ''
  };

  const getAssignedMechanicStatus = (mechanic) => {
    const rawStatus =
      mechanic?.status;
    const statusValue = Number(rawStatus);
    const statusText =
      mechanicStatuses.find((status) => Number(status.id) === statusValue)?.name ||
      'Unknown';
    return {
      value: statusValue,
      text: statusText,
      color: BREAKDOWN_STATUS_COLORS[statusValue] || getStatusColor(statusText)
    };
  };

  useEffect(() => {
    if (isCancelledSelected && cancellationReasons.length === 0) {
      eventService.getCancellationReasons()
        .then(setCancellationReasons)
        .catch((error) => {
          console.error('Failed to load cancellation reasons:', error);
          message.error('Failed to load cancellation reasons');
        });
    }
  }, [cancellationReasons.length, isCancelledSelected]);

  const handleAddNote = async (values) => {
    setNoteSaving(true);
    try {
      await eventService.addNote({
        breakdownId: selectedId,
        note: values.note,
        followUpDate: values.followUp ? values.followUp.toISOString() : null
      });
      message.success('Note added successfully');
      setNoteModalOpen(false);
      noteForm.resetFields();
      await loadDetails();
    } catch (error) {
      console.error('Failed to add event note:', error);
      message.error('Failed to add note');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleStatusSelectChange = (value) => {
    const status = mechanicStatuses.find((item) => Number(item.id) === Number(value));
    const statusName = String(status?.name || '').toLowerCase();
    if (['en route', 'en-route'].includes(statusName)) {
      setEtaDate(null);
      setEtaHour(null);
      setEtaMinute(null);
      setEtaModalOpen(true);
    }
  };

  const handleEtaModalCancel = () => {
    setEtaModalOpen(false);
    setEtaDate(null);
    setEtaHour(null);
    setEtaMinute(null);
    statusForm.setFieldsValue({ status: mechanicDetails?.status });
  };

  const handleUpdateStatus = async (values) => {
    setStatusSaving(true);
    try {
      const assignedMechanic = getCurrentAssignedMechanic();
      const breakdownMechanicId = assignedMechanic?.breakdownMechanicId ?? assignedMechanic?.id ?? mechanicDetails?.breakdownMechanicId;

      if (!breakdownMechanicId || !values.status) {
        message.warning('Mechanic status was not updated: missing mechanic assignment');
      } else {
        const ata = isOnSiteSelected && !mechanicDetails?.ata
          ? dayjs().format('YYYY-MM-DDTHH:mm:ss')
          : undefined;

        setEtaSaving(true);
        try {
          await eventService.updateBreakdownMechanicStatus({
            breakdownMechanicId,
            mechanicModelId: userId,
            status: String(values.status),
            eta: etaValue ? etaValue.format('YYYY-MM-DDTHH:mm:ss') : undefined,
            ata,
            breakdownId: selectedId,
            isMechanic: true
          });
        } catch (etaError) {
          console.error('Failed to update mechanic status:', etaError);
          message.error('Failed to update mechanic status');
        } finally {
          setEtaSaving(false);
        }
      }

      message.success('Event status updated');
      await loadDetails();
    } catch (error) {
      console.error('Failed to update event status:', error);
      message.error('Failed to update event status');
    } finally {
      setStatusSaving(false);
    }
  };

  const resetCompleteBreakdownState = () => {
    completeForm.resetFields();
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedComponent(null);
    setSelectedCause(null);
    setSelectedTyres([]);
    setBreakdownSubcategories([]);
    setBreakdownComponents([]);
    setCompleteCategorySearchTerm('');
    setCompleteCategorySearchValue(undefined);
    setCompleteCategorySearchResults([]);
    setCompleteFormData({});
  };

  const loadCompleteBreakdownLookups = async () => {
    setLoadingCategories(true);
    setLoadingCauses(true);
    try {
      const [categories, causes] = await Promise.all([
        breakdownCategoryService.getCategories(),
        breakdownCategoryService.getCauses()
      ]);
      setBreakdownCategories(categories.filter((category) => category.active));
      setBreakdownCauses(causes.filter((cause) => cause.active));
    } catch (error) {
      console.error('Failed to load completion lookups:', error);
      message.error('Failed to load completion lookups');
    } finally {
      setLoadingCategories(false);
      setLoadingCauses(false);
    }
  };

  const showCompleteBreakdownModal = () => {
    resetCompleteBreakdownState();
    completeForm.setFieldsValue({ breakdownId: currentBreakdownId });
    loadCompleteBreakdownLookups();
    setCompleteModalOpen(true);
  };

  const fetchCompleteSubCategories = async (categoryId) => {
    if (!categoryId) return;

    setLoadingSubcategories(true);
    try {
      const rows = await breakdownCategoryService.getSubCategories(categoryId);
      setBreakdownSubcategories(rows.filter((item) => item.active));
    } catch (error) {
      console.error('Failed to load sub-categories:', error);
      message.error('Failed to load sub-categories');
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const fetchCompleteComponents = async (subCategoryId) => {
    if (!subCategoryId) return;

    setLoadingComponents(true);
    try {
      const rows = await breakdownCategoryService.getComponents(subCategoryId);
      setBreakdownComponents(rows.filter((item) => item.active));
    } catch (error) {
      console.error('Failed to load components:', error);
      message.error('Failed to load components');
    } finally {
      setLoadingComponents(false);
    }
  };

  useEffect(() => {
    if (!completeModalOpen) return undefined;

    const handle = window.setTimeout(() => {
      const term = completeCategorySearchTerm.trim();
      if (!term) {
        setCompleteCategorySearchResults([]);
        setCompleteCategorySearching(false);
        return;
      }

      setCompleteCategorySearching(true);
      Promise.all([
        breakdownCategoryService.searchCategoryCombinations({
          searchTerm: term,
          pageNumber: 1,
          tyres: false
        }),
        breakdownCategoryService.searchCategoryCombinations({
          searchTerm: term,
          pageNumber: 1,
          tyres: true
        })
      ])
        .then(([categoryRows, tyreRows]) => {
          const combined = [...categoryRows, ...tyreRows];
          const seen = new Set();
          setCompleteCategorySearchResults(combined.filter((item) => {
            const key = buildCategorySearchValue(item);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          }));
        })
        .catch((error) => {
          console.error('Failed to search category combinations:', error);
          message.error('Failed to search category combinations');
        })
        .finally(() => setCompleteCategorySearching(false));
    }, 300);

    return () => window.clearTimeout(handle);
  }, [completeCategorySearchTerm, completeModalOpen]);

  const handleCompleteCategorySearchSelect = (value) => {
    const item = completeCategorySearchResults.find((result) => buildCategorySearchValue(result) === value);
    if (!item) return;

    const category = {
      id: item.categoryId,
      category: item.categoryName,
      active: true
    };
    const subCategory = {
      id: item.subCategoryId,
      categoryId: item.categoryId,
      subCategory: item.subCategoryName,
      active: true
    };
    const component = {
      id: item.componentId,
      subCategoryId: item.subCategoryId,
      component: item.componentName,
      active: true
    };

    setBreakdownCategories((previous) => upsertById(previous, category));
    setBreakdownSubcategories((previous) => upsertById(previous, subCategory));
    setBreakdownComponents((previous) => upsertById(previous, component));
    setSelectedCategory(category);
    setSelectedSubCategory(subCategory);
    setSelectedComponent(component);
    setSelectedCause(null);
    setSelectedTyres([]);
    setCompleteCategorySearchValue(value);
    completeForm.setFieldsValue({
      breakdownCategory: category.id,
      breakdownSubCategory: subCategory.id,
      breakdownComponent: component.id,
      cause: undefined
    });
    fetchCompleteSubCategories(category.id);
    fetchCompleteComponents(subCategory.id);
  };

  const handleCompleteCategoryChange = (value) => {
    const category = breakdownCategories.find((item) => item.id === value);
    setCompleteCategorySearchValue(undefined);
    setCompleteCategorySearchTerm('');
    setCompleteCategorySearchResults([]);
    setSelectedCategory(category || null);
    setSelectedSubCategory(null);
    setSelectedComponent(null);
    setSelectedCause(null);
    setSelectedTyres([]);
    setBreakdownComponents([]);
    completeForm.setFieldsValue({
      breakdownSubCategory: undefined,
      breakdownComponent: undefined,
      cause: undefined
    });
    fetchCompleteSubCategories(value);
  };

  const handleCompleteSubCategoryChange = (value) => {
    const subCategory = breakdownSubcategories.find((item) => item.id === value);
    setSelectedSubCategory(subCategory || null);
    setSelectedComponent(null);
    setSelectedCause(null);
    completeForm.setFieldsValue({
      breakdownComponent: undefined,
      cause: undefined
    });
    fetchCompleteComponents(value);
  };

  const handleCompleteComponentChange = (value) => {
    const component = breakdownComponents.find((item) => item.id === value);
    setSelectedComponent(component || null);
    setSelectedCause(null);
    completeForm.setFieldsValue({ cause: undefined });
  };

  const handleCompleteCauseChange = (value) => {
    const cause = breakdownCauses.find((item) => item.id === value);
    setSelectedCause(cause || null);
  };

  const toggleSelectedTyre = (position) => {
    setSelectedTyres((previous) =>
      previous.includes(position)
        ? previous.filter((item) => item !== position)
        : [...previous, position].sort((left, right) => left - right)
    );
  };

  const handleCompleteBreakdownSubmit = (values) => {
    if (selectedCategory?.category?.toUpperCase() === 'TYRES' && selectedTyres.length === 0) {
      message.error('Please select tyre position(s)');
      setTyrePickerOpen(true);
      return;
    }

    setCompleteFormData({
      ...values,
      tyrePosition: selectedTyres.join(',')
    });
    setCompleteModalOpen(false);
    setConfirmCompleteOpen(true);
  };

  const handleConfirmCompleteBreakdown = async () => {
    if (!currentBreakdownId || !selectedCategory || !selectedSubCategory || !selectedComponent) {
      message.error('Category, sub-category, and component are required');
      return;
    }

    setCompleteSaving(true);
    try {
      await breakdownCategoryService.insertBreakdownCategory({
        breakdownId: currentBreakdownId,
        categoryId: selectedCategory.id,
        categoryName: selectedCategory.category,
        subCategoryId: selectedSubCategory.id,
        subCategoryName: selectedSubCategory.subCategory,
        componentId: selectedComponent.id,
        componentName: selectedComponent.component,
        causeId: selectedCause?.id || undefined,
        causeName: selectedCause?.cause || undefined,
        tyrePosition: selectedCategory.category?.toUpperCase() === 'TYRES'
          ? completeFormData.tyrePosition
          : '0'
      });

      await eventService.completeBreakdown({
        breakdownId: currentBreakdownId,
        breakdownCategory: '',
        breakdownSubCategory: '',
        correction: completeFormData.correction,
        faultyAsset: completeFormData.affectedAsset,
        breakdownMechanicId: mechanicDetails?.breakdownMechanicId || '',
        cause: selectedCause?.cause || null,
        ...(selectedCategory.category?.toUpperCase() === 'TYRES' && {
          tyrePosition: completeFormData.tyrePosition
        })
      });

      message.success('Breakdown completed successfully');
      setConfirmCompleteOpen(false);
      resetCompleteBreakdownState();
      await loadDetails();
    } catch (error) {
      console.error('Failed to complete breakdown:', error);
      message.error('Failed to complete breakdown');
    } finally {
      setCompleteSaving(false);
    }
  };

  const getCurrentAssignedMechanic = () =>
    assignedMechanics.find((mechanic) =>
      idsMatch(mechanic.mechanicModelId, userId) ||
      idsMatch(mechanic.mechanicId, userId) ||
      idsMatch(mechanic.id, mechanicDetails?.breakdownMechanicId)
    );

  const handleMechanicActionConfirm = async () => {
    if (!mechanicActionType) return;

    setMechanicActionSaving(true);
    try {
      const userData = getStoredUserData();
      const assignedMechanic = getCurrentAssignedMechanic();

      if (!currentBreakdownId || !mechanicDetails) {
        message.error('Missing mechanic assignment information');
        return;
      }

      if (mechanicActionType === 'accept') {
        const locationResult = await getCurrentLocation();
        const breakdownMechanicId = assignedMechanic?.breakdownMechanicId ?? assignedMechanic?.id ?? mechanicDetails.breakdownMechanicId;

        if (!breakdownMechanicId) {
          message.error('Missing mechanic assignment information');
          return;
        }

        await eventService.acceptMechanic({
          breakdownId: currentBreakdownId,
          breakdownMechanicId,
          mechanicName: userData.unique_name || userName,
          mechanicMobile: mechanicDetails.mobileNum,
          latitude: locationResult?.latitude,
          longitude: locationResult?.longitude
        });
        message.success('Event accepted successfully');
      } else {
        await eventService.declineMechanic({
          breakdownId: currentBreakdownId,
          mechanicId: userData.nameid || userId,
          loginId: userData.LoginId,
          userName: userData.unique_name || userName,
          fullName: userData.unique_name || userName,
          companyName: mechanicDetails.companyName
        });
        message.success('Event declined successfully');
      }

      setMechanicActionType(null);
      await loadDetails();
      await fetchMechanicDetails();
    } catch (error) {
      console.error(`Failed to ${mechanicActionType} event:`, error);
      message.error(mechanicActionType === 'accept' ? 'Failed to accept event' : 'Failed to decline event');
    } finally {
      setMechanicActionSaving(false);
    }
  };

  const handleAssignMechanic = async () => {
    if (!selectedMechanic || !currentBreakdownId) {
      message.error('Please select a mechanic');
      return;
    }

    setAssigningMechanic(true);
    try {
      await eventService.assignMechanicAndSupplier(currentBreakdownId, {
        mechanicId: getMechanicId(selectedMechanic),
        mechanicName: getMechanicDisplayName(selectedMechanic),
        supplierId: selectedMechanic.supplierId,
        companyId: selectedMechanic.companyId,
        companyName: getMechanicCompanyName(selectedMechanic)
      });

      message.success('Mechanic assigned successfully');
      setAssignConfirmOpen(false);
      setSelectedMechanicId(null);
      await loadDetails();
      await fetchAssignedMechanics();
    } catch (error) {
      console.error('Failed to assign event mechanic:', error);
      message.error('Failed to assign mechanic');
    } finally {
      setAssigningMechanic(false);
    }
  };

  const fetchMediaItems = useCallback(async (mediaIds) => {
    if (!Array.isArray(mediaIds) || mediaIds.length === 0) {
      setMedia([]);
      return [];
    }

    const results = await Promise.allSettled(
      mediaIds.map(async (item) => {
        const mediaId = item?.id ?? item?.Id ?? item;
        return eventService.getMedia(mediaId);
      })
    );

    return results
      .filter((result) => result.status === 'fulfilled' && result.value?.base64)
      .map((result) => result.value);
  }, []);

  const loadMedia = useCallback(async () => {
    if (!currentBreakdownId) {
      setMedia([]);
      return;
    }

    setLoadingMedia(true);
    try {
      imageCacheManager.clearExpiredCache().catch(console.error);

      const freshMediaIds = await eventService.getMediaIds(currentBreakdownId);
      const cacheIsValid = await isCacheValid(currentBreakdownId);

      if (cacheIsValid) {
        const cachedData = await getCachedImages(currentBreakdownId);
        if (cachedData && compareImageIds(cachedData.imageIds, freshMediaIds)) {
          setMedia(cachedData.images);
          return;
        }
      }

      const fetchedMedia = await fetchMediaItems(freshMediaIds);
      setMedia(fetchedMedia);

      if (fetchedMedia.length > 0) {
        cacheImages(currentBreakdownId, fetchedMedia, freshMediaIds);
      }
    } catch (error) {
      console.error('Failed to load event media:', error);
      message.error('Failed to load media');
    } finally {
      setLoadingMedia(false);
    }
  }, [currentBreakdownId, fetchMediaItems]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const canAccessDocuments = usePermissionAccess(CREATE_DOCUMENT_PERMISSION);
  const documentCompanyId = breakdown?.companyModelId;

  const loadDocumentFiles = useCallback(async () => {
    if (!currentBreakdownId || !canAccessDocuments) {
      setDocumentFiles([]);
      return;
    }

    setLoadingDocuments(true);
    try {
      setDocumentFiles(await documentService.getEventDocumentFiles(currentBreakdownId));
    } catch (error) {
      console.error('Failed to load event documents:', error);
      message.error('Failed to load documents');
      setDocumentFiles([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [currentBreakdownId, canAccessDocuments]);

  useEffect(() => {
    loadDocumentFiles();
  }, [loadDocumentFiles]);

  const handleMediaUpload = async (file) => {
    if (!currentBreakdownId) {
      message.error('Event details unavailable');
      return false;
    }

    setUploadingMedia(true);
    try {
      const mediaType = getMediaTypeFromFile(file);
      let processedFile = file;

      if (mediaType.startsWith('image')) {
        const fileAge = Date.now() - file.lastModified;
        const imageMinutes = 30;
        const isCameraImage = fileAge < imageMinutes * 60 * 1000;

        if (isCameraImage) {
          message.loading('Adding location and time to image...', 0);
          processedFile = await addLocationTimeOverlay(file);
          message.destroy();
        }
      }

      const base64 = await readFileAsBase64(processedFile);
      const extension = String(processedFile.name || '').split('.').pop();
      await eventService.addMedia([
        {
          BreakdownId: currentBreakdownId,
          Image: base64,
          MediaType: mediaType || EXT_TO_MIME[String(extension).toLowerCase()] || 'application/octet-stream',
          Extension: extension,
          LastModifiedOn: null,
          Deleted: null,
          DeletedOn: null,
          CreatedOn: null
        }
      ]);

      await loadMedia();

      message.success(
        mediaType.startsWith('image')
          ? 'Image uploaded successfully'
          : mediaType.startsWith('video')
            ? 'Video uploaded successfully'
            : 'PDF uploaded successfully'
      );

      return true;
    } catch (error) {
      message.destroy();
      console.error('Failed to upload event media:', error);
      message.error('Failed to upload media');
      return false;
    } finally {
      setUploadingMedia(false);
    }
  };

  if (loading) {
    return (
      <div className="event-details-page">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (!breakdown) {
    return (
      <div className="event-details-page">
        <Empty description="Event details unavailable" />
      </div>
    );
  }

  const reference = firstPresent(
    breakdown.breakdownReference,
    breakdown.reference
  );
  const currentStatusName = firstPresent(
    currentStatus?.statusName,
    breakdown.eventStatusName,
    breakdown.statusDescription,
    'Unknown'
  );
  const locationAddress = firstPresent(breakdown.breakdownAddress, breakdown.address);
  const locationLatitude = breakdown.latitude;
  const locationLongitude = breakdown.longitude;
  const hasLocation = locationLatitude && locationLongitude;
  const driverPhone = firstPresent(breakdown.driverCell, breakdown.contactNumber);
  const vehicleDisplay = [breakdown.make, breakdown.model, breakdown.year].filter(Boolean).join(' ');
  const trailerDisplay = [breakdown.trailerMake, breakdown.trailerModel, breakdown.trailerYear].filter(Boolean).join(' ');
  const hasTrailerDetails = Boolean(
    breakdown.trailerReg ||
    breakdown.trailerRegistration ||
    breakdown.trailerFleetNumber ||
    breakdown.trailerVin ||
    breakdown.trailerMake ||
    breakdown.trailerModel ||
    breakdown.secondTrailerReg ||
    breakdown.secondTrailerFleetNumber ||
    breakdown.secondTrailerVin
  );

  const handleCallDriver = () => {
    const dialablePhone = getDialablePhone(driverPhone);

    if (!dialablePhone) {
      message.warning('No driver contact number available');
      return;
    }

    window.location.href = `tel:${dialablePhone}`;
  };

  const handleShare = async () => {
    const shareText = buildEventShareText({ reference, breakdown, address: locationAddress });
    const shareUrl = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Event ${reference || ''}`.trim(),
          text: shareText,
          url: shareUrl
        });
        return;
      }

      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      message.success('Event details copied to clipboard');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Failed to share event details:', error);
      message.error('Share cancelled or failed');
    }
  };

  const customDetailsContent = (
    <div className="event-custom-details">
      {customDisplayEntries.length > 0 ? (
        <DetailTable>
          {customDisplayEntries.map(([label, value]) => (
            <DetailValue key={label} label={label} values={[formatCustomDisplayValue(value)]} />
          ))}
        </DetailTable>
      ) : null}

      {customDisplayEntries.length === 0 ? (
        <Empty description="No custom details for this event" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : null}
    </div>
  );

  const tabItems = [
    {
      key: 'details',
      label: <Space><InfoCircleOutlined />Details</Space>,
      children: (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Card className="event-details-card">
            <Collapse
              className="event-detail-collapse"
              defaultActiveKey={['vehicle', 'event']}
              bordered={false}
              items={[
                {
                  key: 'vehicle',
                  label: <Space><CarOutlined />Vehicle Information</Space>,
                  children: (
                    <DetailTable>
                      <DetailValue label="Registration" values={[breakdown.registrationNumber]} />
                      <DetailValue label="Fleet" values={[breakdown.fleetNumber]} />
                      <DetailValue label="VIN" values={[breakdown.vinNumber]} />
                      <DetailValue label="Make & Model" values={[vehicleDisplay]} />
                      <DetailValue label="Company" values={[breakdown.companyName, breakdown.customerName]} />
                    </DetailTable>
                  )
                },
                {
                  key: 'event',
                  label: <Space><FileTextOutlined />Event Information</Space>,
                  children: (
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <DetailTable>
                        <DetailValue label="Description" values={[breakdown.description]} />
                        <DetailValue label="Address">
                          <Space direction="vertical" size={8} className="event-detail-action-stack">
                            <Space align="start">
                              <EnvironmentOutlined />
                              <Text>{cleanValue(locationAddress)}</Text>
                            </Space>
                            {(hasLocation || locationAddress) ? (
                              <Button
                                type="primary"
                                icon={<EnvironmentOutlined />}
                                size="small"
                                onClick={() => openNavigation({
                                  latitude: locationLatitude,
                                  longitude: locationLongitude,
                                  address: locationAddress
                                })}
                              >
                                Navigate
                              </Button>
                            ) : null}
                          </Space>
                        </DetailValue>
                        <DetailValue label="Driver" values={[breakdown.driverAlias, breakdown.driverModelName]} />
                        <DetailValue label="Contact">
                          {driverPhone ? (
                            <Space direction="vertical" size={8} className="event-detail-action-stack">
                              <Button
                                type="primary"
                                icon={<PhoneOutlined />}
                                size="small"
                                onClick={handleCallDriver}
                              >
                                Call Driver
                              </Button>
                              <Text>{driverPhone}</Text>
                            </Space>
                          ) : (
                            'N/A'
                          )}
                        </DetailValue>
                        <DetailValue label="Share">
                          <Button
                            type="primary"
                            icon={<ShareAltOutlined />}
                            size="small"
                            onClick={handleShare}
                          >
                            Share
                          </Button>
                        </DetailValue>
                        <DetailValue label="Reported" values={[formatDate(breakdown.dateTimeOffset)]} />
                      </DetailTable>
                    </Space>
                  )
                },
                hasTrailerDetails ? {
                  key: 'trailer',
                  label: <Space><CarOutlined />Trailer Information</Space>,
                  children: (
                    <DetailTable>
                      <DetailValue label="Registration" values={[breakdown.trailerReg, breakdown.trailerRegistration]} />
                      <DetailValue label="Fleet" values={[breakdown.trailerFleetNumber]} />
                      <DetailValue label="VIN" values={[breakdown.trailerVin]} />
                      <DetailValue label="Make & Model" values={[trailerDisplay]} />
                    </DetailTable>
                  )
                }
                  : null,
                {
                  key: 'custom',
                  label: <Space><EditOutlined />Custom Details</Space>,
                  children: customDetailsContent
                },
                {
                  key: 'history',
                  label: <Space><HistoryOutlined />History</Space>,
                  children: loadingHistory ? (
                    <div className="event-history-loading">
                      <Spin tip="Loading event history..." />
                    </div>
                  ) : historyRows.length > 0 ? (
                    <List
                      className="event-history-list"
                      dataSource={historyRows}
                      renderItem={(historyItem, index) => (
                        <List.Item key={historyItem.id || index}>
                          <EventCard event={historyItem} mechanicStatuses={mechanicStatuses} showStatus={false} />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Empty description="No event history available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )
                }
              ].filter(Boolean)}
            />
          </Card>
          <BreakdownCategoryPanels breakdownId={currentBreakdownId} />
        </Space>
      )
    },
    {
      key: 'notes',
      label: <Space><MessageOutlined />Notes</Space>,
      children: (
        <Card className="event-details-card event-notes-card">
          {notes.length === 0 ? (
            <Empty description="No notes available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div className="event-notes-container">
              {notes.map((note, index) => {
                const isStatusUpdate = note.recordType === 'S';
                const isCurrentUser = note.userName === userName;

                return (
                  <div
                    key={note.id || `${note.createdDateTime}-${index}`}
                    className={`event-note-bubble ${isCurrentUser ? 'event-note-bubble--self' : 'event-note-bubble--other'} ${isStatusUpdate ? 'event-note-bubble--status' : ''}`}
                  >
                    {!isStatusUpdate ? (
                      <div className="event-note-bubble__header">
                        <Text strong>{isCurrentUser ? 'You' : note.userName || 'Unknown'}</Text>
                      </div>
                    ) : null}
                    <div className="event-note-bubble__content">
                      <Text>{isStatusUpdate ? `Status: ${note.note}` : note.note}</Text>
                    </div>
                    <div className="event-note-bubble__footer">
                      <Text type="secondary" className="event-note-bubble__time">
                        {formatDate(note.createdDateTime, false)}
                      </Text>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )
    },
    {
      key: 'media',
      label: <Space><PictureOutlined />Media</Space>,
      children: (
        <Card className="event-details-card event-media-card">
          <div className="event-media-upload">
            <Upload
              accept="image/*,video/*,application/pdf"
              listType="picture-card"
              customRequest={({ file, onSuccess, onError }) => {
                handleMediaUpload(file)
                  .then((uploaded) => {
                    if (uploaded) {
                      onSuccess?.('ok');
                    } else {
                      onError?.(new Error('Upload failed'));
                    }
                  })
                  .catch((error) => onError?.(error));
              }}
              showUploadList={false}
              multiple
            >
              {uploadingMedia ? (
                <div>
                  <Spin />
                  <div style={{ marginTop: 8 }}>Uploading...</div>
                </div>
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload From Gallery/Camera</div>
                </div>
              )}
            </Upload>
          </div>

          <div className="event-media-container">
            {loadingMedia ? (
              <div className="event-media-loading">
                <Spin size="large" />
                <Text type="secondary">Loading media...</Text>
              </div>
            ) : media.length > 0 ? (
              <div className="event-media-grid">
                {media.map((item, index) => {
                  const src = getMediaDataUrl(item);
                  const mediaType = String(item.mediaType || '').toLowerCase();

                  if (mediaType.startsWith('image')) {
                    return (
                      <Image
                        key={item.id || index}
                        width={90}
                        height={90}
                        style={{ objectFit: 'cover' }}
                        src={src}
                        alt={`Media ${index + 1}`}
                      />
                    );
                  }

                  if (mediaType.startsWith('video')) {
                    return (
                      <button
                        key={item.id || index}
                        type="button"
                        className="event-media-video"
                        onClick={() => setVideoPreview({ src, title: `Video ${index + 1}` })}
                        aria-label={`Open video ${index + 1}`}
                      >
                        <video src={src} preload="metadata" playsInline muted />
                        <span className="event-media-video__play">
                          <PlayCircleOutlined />
                        </span>
                      </button>
                    );
                  }

                  if (mediaType.startsWith('pdf') || mediaType === 'application/pdf') {
                    return (
                      <a
                        key={item.id || index}
                        href={src}
                        target="_blank"
                        rel="noreferrer"
                        className="event-media-pdf"
                      >
                        <FileImageOutlined />
                        <span>PDF</span>
                      </a>
                    );
                  }

                  return null;
                })}
              </div>
            ) : (
              <div className="event-media-empty">
                <FileImageOutlined />
                <Text type="secondary">No media available. Upload media using the button above.</Text>
              </div>
            )}
          </div>
        </Card>
      )
    },
    canAccessDocuments ? {
      key: 'documents',
      label: <Space><FileTextOutlined />Documents</Space>,
      children: (
        <Card className="event-details-card event-media-card">
          <Button
            type="primary"
            block
            icon={<FileTextOutlined />}
            disabled={!currentBreakdownId || !documentCompanyId}
            onClick={() => setCreateDocumentOpen(true)}
          >
            Create Document
          </Button>

          <div className="event-media-container">
            {loadingDocuments ? (
              <div className="event-media-loading">
                <Spin size="large" />
                <Text type="secondary">Loading documents...</Text>
              </div>
            ) : documentFiles.length > 0 ? (
              <div className="event-media-grid">
                {documentFiles.map((file, index) => (
                  <a
                    key={file.id || index}
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="event-media-pdf"
                    title={file.name}
                  >
                    <FileTextOutlined />
                    <span>{file.name}</span>
                  </a>
                ))}
              </div>
            ) : (
              <div className="event-media-empty">
                <FileTextOutlined />
                <Text type="secondary">No documents on this event yet.</Text>
                <Text type="secondary" className="event-documents__hint">
                  A document only appears here once it is completed, because that is when its PDF is
                  generated. Anything saved as in progress is waiting in Documents &gt; Library.
                </Text>
              </div>
            )}
          </div>
        </Card>
      )
    } : null,
    {
      key: 'status',
      label: <Space><ReloadOutlined />Status</Space>,
      children: (
        <Card className="event-details-card event-status-card">
          <Title level={5}>Update Your Status</Title>
          <Text>Select your current status for this event:</Text>
          <Form form={statusForm} layout="vertical" onFinish={handleUpdateStatus}>
            <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Status is required' }]}>
              <Select
                disabled={!eventTypeId}
                placeholder={eventTypeId ? 'Select status' : 'No event type assigned'}
                onChange={handleStatusSelectChange}
                options={mechStatusOptions.filter((option) => option.label !== 'Remove' && option.label !== 'Completed')}
              />
            </Form.Item>
            {isCancelledSelected ? (
              <Form.Item name="reason" label="Cancellation Reason" rules={[{ required: true, message: 'Cancellation reason is required' }]}>
                <Select
                  placeholder="Select reason"
                  options={cancellationReasons.map((reason) => ({
                    label: reason.description,
                    value: reason.description
                  }))}
                />
              </Form.Item>
            ) : null}
            {isWorkshopSelected ? (
              <Form.Item name="reason" label="Case Number" rules={[{ required: true, message: 'Case number is required' }]}>
                <Input />
              </Form.Item>
            ) : null}
            <Space wrap>
              <Button type="primary" htmlType="submit" loading={statusSaving}>
                Set Status
              </Button>
              {canCompleteBreakdown ? (
                <Button loading={completeSaving} onClick={showCompleteBreakdownModal} disabled={!eventTypeId}>
                  Complete Event
                </Button>
              ) : null}
            </Space>
          </Form>
        </Card>
      )
    },
    canSeeSuppliers ? {
      key: 'suppliers',
      label: <Space><ToolOutlined />Suppliers</Space>,
      children: (
        <Card className="event-details-card event-suppliers-card" title={<Space><ToolOutlined />Suppliers</Space>}>
          <div className="event-suppliers-section">
            <Text strong>Assigned Mechanics</Text>
            {assignedMechanicsLoading ? (
              <div className="event-suppliers-loading">
                <Spin />
                <Text type="secondary">Loading assigned mechanics...</Text>
              </div>
            ) : assignedMechanics.length > 0 ? (
              <List
                className="event-assigned-mechanics"
                dataSource={assignedMechanics}
                renderItem={(mechanic, index) => {
                  const status = getAssignedMechanicStatus(mechanic);
                  return (
                    <List.Item key={getMechanicId(mechanic) || mechanic.breakdownMechanicId || index}>
                      <Space>
                        <UserOutlined />
                        <Text strong>{getMechanicDisplayName(mechanic)}</Text>
                      </Space>
                      <Badge color={status.color} text={status.text} />
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty description="No mechanics assigned yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>

          <div className="event-suppliers-section">
            <Text>Select a mechanic to assign to this event:</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select mechanic"
              value={selectedMechanicId}
              loading={mechanicOptionsLoading}
              showSearch
              optionFilterProp="label"
              onChange={setSelectedMechanicId}
              options={filteredMechanicOptions.map((mechanic) => ({
                label: `${getMechanicDisplayName(mechanic)}${getMechanicCompanyName(mechanic) ? ` - ${getMechanicCompanyName(mechanic)}` : ''}`,
                value: getMechanicId(mechanic)
              }))}
            />
            <Button
              type="primary"
              block
              disabled={!selectedMechanicId || assigningMechanic}
              loading={assigningMechanic}
              onClick={() => setAssignConfirmOpen(true)}
            >
              Assign Mechanic
            </Button>
          </div>
        </Card>
      )
    } : null,
  ].filter(Boolean);

  return (
    <div className="event-details-page">
      <PullToRefresh onRefresh={handleRefresh}>
        <Card className="event-details-summary">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4} className="event-details-reference">{reference}</Title>
            <Space wrap>
              <Badge color={getStatusColor(currentStatusName)} text={currentStatusName} />
            </Space>
          </div>
          {mechanicDetails ? (
            <div className="event-mechanic-status">
              <div className="event-mechanic-status__row">
                <Text type="secondary">Your Status:</Text>
                <Badge
                  color={BREAKDOWN_STATUS_COLORS[mechanicDetails.status] || '#999'}
                  text={mechanicStatusName || (Number(mechanicDetails.status) === 2 ? 'Pending' : 'Unknown')}
                />
              </div>
              {isMechanicPending ? (
                <div className="event-mechanic-status__actions">
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => setMechanicActionType('accept')}
                  >
                    Accept
                  </Button>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => setMechanicActionType('decline')}
                  >
                    Decline
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </Card>

        <Tabs
          className="event-details-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarGutter={12}
          items={tabItems}
        />

        <CreateDocumentModal
          open={createDocumentOpen}
          companyId={documentCompanyId}
          onClose={() => setCreateDocumentOpen(false)}
          onPick={(template) => {
            setCreateDocumentOpen(false);
            navigate(`/documents/fill/${template.id}`, {
              state: {
                templateName: template.name,
                lockedCompanyId: documentCompanyId,
                relationId: currentBreakdownId,
                returnTo: location.pathname,
              },
            });
          }}
        />

        {activeTab === 'notes' ? (
          <div className="event-floating-note-button">
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => setNoteModalOpen(true)}
            />
          </div>
        ) : null}

        <Modal
          title="Set Your ETA"
          open={etaModalOpen}
          onOk={() => {
            setEtaModalOpen(false);
            statusForm.submit();
          }}
          onCancel={handleEtaModalCancel}
          okText="Set Status"
          cancelText="Cancel"
          okButtonProps={{ disabled: !etaValue, loading: statusSaving }}
          width={window.innerWidth < 768 ? '95%' : 340}
          destroyOnHidden
        >
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: 16, fontSize: 16 }}>
              Please select your estimated date and time of arrival.
            </p>
            <DatePicker
              style={{ width: '100%' }}
              value={etaDate}
              onChange={setEtaDate}
              format="YYYY-MM-DD"
              showNow={false}
              placeholder="Select date"
              disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'))}
              getPopupContainer={() => document.body}
              classNames={{ popup: { root: 'eta-datepicker-popup' } }}
            />

            <div style={{ marginTop: 16 }}>
              <Text strong>Time</Text>
              <div style={{ marginTop: 8 }}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Hour"
                  value={etaHour ?? undefined}
                  onChange={(hour) => {
                    setEtaHour(hour);
                    setEtaMinute((prevMinute) => (prevMinute === null ? 0 : prevMinute));
                  }}
                  options={Array.from({ length: 24 }, (_, hour) => ({
                    value: hour,
                    label: String(hour).padStart(2, '0')
                  }))}
                  getPopupContainer={() => document.body}
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Minute"
                  value={etaMinute ?? undefined}
                  onChange={setEtaMinute}
                  options={Array.from({ length: 12 }, (_, index) => ({
                    value: index * 5,
                    label: String(index * 5).padStart(2, '0')
                  }))}
                  getPopupContainer={() => document.body}
                />
              </div>
            </div>
          </div>
        </Modal>

        <Modal
          title="Add Event Note"
          open={noteModalOpen}
          onCancel={() => {
            if (!noteSaving) setNoteModalOpen(false);
          }}
          onOk={() => noteForm.submit()}
          confirmLoading={noteSaving}
          destroyOnHidden
        >
          <Form form={noteForm} layout="vertical" onFinish={handleAddNote}>
            <Form.Item name="note" label="Note" rules={[{ required: true, message: 'Please enter a note' }]}>
              <Input.TextArea rows={4} placeholder="Type your note here..." />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Complete Event"
          open={completeModalOpen}
          onCancel={() => {
            if (!completeSaving) setCompleteModalOpen(false);
          }}
          footer={null}
          destroyOnHidden
          width={window.innerWidth < 768 ? '95%' : 560}
          styles={{
            body: {
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto',
            },
          }}
        >
          <Form
            form={completeForm}
            layout="vertical"
            onFinish={handleCompleteBreakdownSubmit}
            requiredMark="optional"
          >
            <Form.Item name="breakdownId" hidden>
              <Input type="hidden" />
            </Form.Item>

            <Form.Item label="Search Category Combination">
              <Select
                allowClear
                classNames={{
                  popup: {
                    root: 'complete-category-search-dropdown'
                  }
                }}
                filterOption={false}
                loading={completeCategorySearching}
                notFoundContent={completeCategorySearching ? <Spin size="small" /> : null}
                onChange={(value) => {
                  if (!value) {
                    setCompleteCategorySearchValue(undefined);
                    return;
                  }
                  handleCompleteCategorySearchSelect(value);
                }}
                onClear={() => {
                  setCompleteCategorySearchTerm('');
                  setCompleteCategorySearchValue(undefined);
                  setCompleteCategorySearchResults([]);
                }}
                onSearch={setCompleteCategorySearchTerm}
                options={completeCategorySearchResults.map((item) => ({
                  label: buildCategorySearchLabel(item),
                  value: buildCategorySearchValue(item)
                }))}
                placeholder="Search category, sub-category, or component"
                showSearch
                value={completeCategorySearchValue}
                virtual={false}
              />
            </Form.Item>

            <Form.Item
              name="breakdownCategory"
              label="Event Category"
              rules={[{ required: true, message: 'Please select a breakdown category' }]}
            >
              <Select
                loading={loadingCategories}
                onChange={handleCompleteCategoryChange}
                optionFilterProp="label"
                options={breakdownCategories.map((category) => ({
                  label: category.category,
                  value: category.id
                }))}
                placeholder="Select event category"
                showSearch
              />
            </Form.Item>

            <Form.Item
              name="breakdownSubCategory"
              label="event Sub Category"
              rules={[{ required: true, message: 'Please select a event sub-category' }]}
            >
              <Select
                disabled={!selectedCategory}
                loading={loadingSubcategories}
                onChange={handleCompleteSubCategoryChange}
                optionFilterProp="label"
                options={breakdownSubcategories.map((subcategory) => ({
                  label: subcategory.subCategory,
                  value: subcategory.id
                }))}
                placeholder="Select event sub-category"
                showSearch
              />
            </Form.Item>

            <Form.Item
              name="breakdownComponent"
              label="Component"
              rules={[{ required: true, message: 'Please select a component' }]}
            >
              <Select
                disabled={!selectedSubCategory}
                loading={loadingComponents}
                onChange={handleCompleteComponentChange}
                optionFilterProp="label"
                options={breakdownComponents.map((component) => ({
                  label: component.component,
                  value: component.id
                }))}
                placeholder="Select component"
                showSearch
              />
            </Form.Item>

            <Form.Item name="cause" label="Cause (Optional)">
              <Select
                allowClear
                disabled={!selectedComponent}
                loading={loadingCauses}
                onChange={handleCompleteCauseChange}
                optionFilterProp="label"
                options={breakdownCauses.map((cause) => ({
                  label: cause.cause,
                  value: cause.id
                }))}
                placeholder="Select cause (optional)"
                showSearch
              />
            </Form.Item>

            {selectedCategory?.category?.toUpperCase() === 'TYRES' ? (
              <Form.Item label="Tyre Position" required>
                <Button block onClick={() => setTyrePickerOpen(true)}>
                  {selectedTyres.length
                    ? `Tyre positions: ${selectedTyres.join(', ')}`
                    : 'Select tyre positions'}
                </Button>
              </Form.Item>
            ) : null}

            <Form.Item
              name="correction"
              label="Correction"
              rules={[{ required: true, message: 'Please provide correction details' }]}
            >
              <Input.TextArea
                maxLength={500}
                placeholder="Enter details about how the breakdown was corrected..."
                rows={4}
                showCount
              />
            </Form.Item>

            <Form.Item
              name="affectedAsset"
              label="Affected Asset"
              rules={[{ required: true, message: 'Please select the affected asset' }]}
            >
              <Select
                options={[
                  { label: 'Horse', value: 'Truck' },
                  { label: 'Trailer 1', value: 'Trailer1' },
                  { label: 'Trailer 2', value: 'Trailer2' }
                ]}
                placeholder="Select affected asset"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setCompleteModalOpen(false)}>
                  Cancel
                </Button>
                <Button htmlType="submit" loading={completeSaving} type="primary">
                  Submit
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Select Tyre Position"
          open={tyrePickerOpen}
          onCancel={() => setTyrePickerOpen(false)}
          footer={(
            <Space style={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
              <Button onClick={() => setTyrePickerOpen(false)}>Cancel</Button>
              <Button
                disabled={!selectedTyres.length}
                onClick={() => setTyrePickerOpen(false)}
                type="primary"
              >
                Done
              </Button>
            </Space>
          )}
          width={720}
          destroyOnHidden
        >
          <TyrePositionPicker
            selectedTyres={selectedTyres}
            onToggleTyre={toggleSelectedTyre}
          />
        </Modal>

        <Modal
          title="Confirm Complete event"
          open={confirmCompleteOpen}
          onOk={handleConfirmCompleteBreakdown}
          onCancel={() => {
            if (!completeSaving) {
              setConfirmCompleteOpen(false);
              setCompleteModalOpen(true);
            }
          }}
          okText="Complete"
          cancelText="Back"
          confirmLoading={completeSaving}
          maskClosable={!completeSaving}
          closable={!completeSaving}
        >
          <p>Are you sure you want to complete this event?</p>
          <p>This action will mark the event as resolved.</p>
        </Modal>

        <Modal
          title={videoPreview?.title || 'Video'}
          open={Boolean(videoPreview)}
          onCancel={() => setVideoPreview(null)}
          footer={null}
          width="95%"
          centered
          destroyOnHidden
        >
          {videoPreview?.src ? (
            <video
              src={videoPreview.src}
              controls
              autoPlay
              playsInline
              style={{ width: '100%', maxHeight: '70vh', background: '#000' }}
              onError={() => message.error('Failed to play video. The video format may not be supported on this device.')}
            />
          ) : null}
        </Modal>

        <Modal
          title={mechanicActionType === 'accept' ? 'Accept Event' : 'Decline Event'}
          open={Boolean(mechanicActionType)}
          onOk={handleMechanicActionConfirm}
          onCancel={() => {
            if (!mechanicActionSaving) setMechanicActionType(null);
          }}
          okText={mechanicActionType === 'accept' ? 'Yes, Accept' : 'Yes, Decline'}
          cancelText="Cancel"
          confirmLoading={mechanicActionSaving}
          maskClosable={!mechanicActionSaving}
          closable={!mechanicActionSaving}
          okButtonProps={{ danger: mechanicActionType === 'decline' }}
        >
          <p>
            Are you sure you want to {mechanicActionType === 'accept' ? 'accept' : 'decline'} this event?
          </p>
        </Modal>

        <Modal
          title="Confirm Mechanic Assignment"
          open={assignConfirmOpen}
          onOk={handleAssignMechanic}
          onCancel={() => {
            if (!assigningMechanic) setAssignConfirmOpen(false);
          }}
          okText="Assign Mechanic"
          confirmLoading={assigningMechanic}
          maskClosable={!assigningMechanic}
          closable={!assigningMechanic}
        >
          <p>
            Assign {selectedMechanic ? getMechanicDisplayName(selectedMechanic) : 'selected mechanic'}
            {selectedMechanic && getMechanicCompanyName(selectedMechanic) ? ` (${getMechanicCompanyName(selectedMechanic)})` : ''} to this event?
          </p>
          <p>This will notify the selected mechanic and link them to this event.</p>
        </Modal>
      </PullToRefresh>
    </div>
  );
};

export default EventDetails;
